/* eslint-disable react/no-array-index-key */
import { useDispatch, useSelector } from 'react-redux';
import React, { ReactElement, useEffect, useState } from 'react';
import Image from 'next/image';
import fa from 'locales/fa.json';
import ScrollContainer from 'react-indiana-drag-scroll';
import Card from 'components/Common/Card';
import { CourseType } from 'types/course.type';
import Attaches from './attaches';
import Information from './Information';
import LessonsList from 'components/Common/LessonsList';
import LoadingBox from 'components/Common/LoadingBox';
import { getSearchDataAction } from 'store/course/course.action';
import Comments from 'components/Common/Comments';
import Questions from 'components/Common/Questions';

const Course: React.FC<{ course: CourseType }> = ({ course }) => {
  const [tabs, setTabs] = useState([]);
  const dispatch = useDispatch();
  const searchData = useSelector((state) => state.course.searchData);
  const [slideId, setSlideId] = useState('lessons');
  const categories = course?.categories.map((k) => k.title);
  const error = useSelector((state) => state.course.searchDataError);
  const similarCourses =
    searchData?.workshops &&
    [...searchData?.workshops]
      .filter((item) => categories?.includes(item.category?.title))
      .filter((item) => item.id !== course?.id)
      .slice(0, 5);

  useEffect(() => {
    const rawTabs = ['comments', 'lessons'];
    !!course?.ask_teacher && rawTabs.unshift('questions');
    course?.attaches.length && course.registered && rawTabs.unshift('attaches');
    setTabs(rawTabs);
  }, [course]);

  const reloadSearchData = (): void => {
    dispatch(getSearchDataAction());
  };

  const relatedCourses = (): ReactElement =>
    !!categories?.length && (
      <div className="w-screen md:w-[600px] xl:w-full bg-white rounded-[8px] min-h-[200px] overflow-hidden">
        <h3 className="h-[70px] center w-full font-bold text-[22px] m-[0px]">
          {fa.course.relatedCourses}
        </h3>

        <LoadingBox data={course} error={error} reload={reloadSearchData}>
          <ScrollContainer className="flex overflow-auto">
            {similarCourses?.map((item) => (
              <div className="scale-[.8] m-[-27px]" key={item.id}>
                <Card data={item} />
              </div>
            ))}
          </ScrollContainer>
        </LoadingBox>
      </div>
    );

  return (
    <div className="bg-gray-0 duration-300 min-h-screen flex xl:block flex-col center">
      {/* title and description */}
      <div className=" w-full md:w-[600px] xl:px-[370px] py-[20px] xl:justify-self-start xl:w-full ">
        <div className="w-full bg-white mt-[20px] md:mt-0 md:min-h-[500px] rounded-[8px]">
          <h1 className="p-[16px] text-center center w-full font-bold text-[26px] m-[0px]">
            {course?.title}
          </h1>
          <Image
            src={course?.image}
            layout="responsive"
            alt={course?.title}
            width={530}
            height={300}
            priority
          />
          <div
            className="mx-[40px] py-[50px] text-[16px]"
            dangerouslySetInnerHTML={{ __html: course?.description }}
          />
        </div>
        <div className="hidden xl:block mt-[16px]">{relatedCourses()}</div>
      </div>

      {/* information */}
      <div className="w-full border-b pb-[16px] xl:pb-0 border-b-gray-1 md:w-[600px] xl:overflow-auto xl:fixed left-0 top-[70px]  xl:w-[350px] xl:h-[calc(100%-70px)] bg-white z-10">
        <h3 className="h-[48px] m-0 center w-full border-b-gray-1 border-b font-bold text-[18px]">
          {fa.course.info}
        </h3>

        <Information course={course} />
      </div>

      <div className="w-full md:w-[600px]   xl:fixed right-0 top-[70px] bg-white xl:w-[350px] xl:h-[calc(100%-70px)]">
        <div
          className={`h-[48px] center w-full flex text-[${
            tabs.length > 3 ? 13 : 16
          }px] md:text-[${
            tabs.length > 3 ? 14 : 18
          }px] flex-row-reverse border-b-gray-1 border-b text-gray-3`}
        >
          {tabs.map((item, index) => (
            <span key={item} aria-hidden="true" onClick={(): void => setSlideId(item)}>
              <span
                className={`mx-[10px] link ${slideId === item && 'font-bold text-black'}`}
              >
                {fa.course[item]}
              </span>
              {index !== 0 && '/'}
            </span>
          ))}
        </div>
        <div className="overflow-auto xl:h-[90%] toRight mb-[40px] xl:mb-0">
          {slideId === 'lessons' && <LessonsList course={course} />}
          {slideId === 'attaches' && <Attaches data={course?.attaches} />}
          {slideId === 'questions' && (
            <Questions
              data={course?.questions}
              register={course?.registered}
              notAllowToAsk
            />
          )}
          {slideId === 'comments' && (
            <Comments
              data={course?.comments}
              id={course?.id}
              type="workshops"
              register={course?.registered}
            />
          )}
        </div>
      </div>

      <div className="block xl:hidden mt-[10px]">{relatedCourses()}</div>
    </div>
  );
};

export default Course;
